import { useState } from "react";
import {
  useCreateEigenbeleg,
  useDeletePayout,
  useMemberBelege,
} from "../../hooks/useTeam";
import { usePassendeBankzeilen, useBankzeileZuordnen } from "../../hooks/useBelege";
import type { HelferBeleg } from "../../lib/types";
import type { TeamOwner, TeamPayroll, TeamPayout } from "../../lib/team";
import { euro, shortDate } from "../../lib/team";
import { Dialog, DialogActions, Field } from "./TeamDialogs";
import DokumentLightbox, { type Dokument } from "../DokumentLightbox";
import { belegDateiUrl } from "../../lib/belege";
import { BelegBezahltDialog, NachweisAktion, PayoutDialog, zahlwegLabel } from "./AuszahlungDialoge";

/** Der Zahlungsnachweis in der Lightbox — Seitenbilder vom Server, wie jeder andere Beleg. */
function nachweisDok(memberId: string, payout: TeamPayout): Dokument {
  const basis = `/api/kontor/team/member/${encodeURIComponent(memberId)}/payout/${encodeURIComponent(payout.id)}/nachweis`;
  return {
    titel: `Zahlungsnachweis ${payout.nachweis_nummer}`,
    unterzeile: shortDate(payout.zahlungsdatum),
    url: `${basis}/pdf`,
    seitenZahlUrl: `${basis}/seiten`,
    seiteUrl: (seite, breite) => `${basis}/seite/${seite}?w=${breite}`,
  };
}

export function MemberPayroll({ memberId, memberName, year, payroll, payouts, owners }: {
  memberId: string;
  memberName?: string;
  year: number;
  payroll: TeamPayroll;
  payouts: TeamPayout[];
  owners: TeamOwner[];
}) {
  const remove = useDeletePayout(memberId);
  const [dok, setDok] = useState<Dokument>(null);
  // Die Belege werden hier EINMAL geladen und an beide Sektionen gereicht:
  // die Auszahlungen zeigen den Beleg, der an ihnen haengt, und darunter
  // stehen nur noch die Rechnungen OHNE Auszahlung. Vorher lief jede bezahlte
  // Rechnung doppelt durchs Bild — einmal als Auszahlung, einmal als Beleg.
  const belegeQuery = useMemberBelege(memberId, year);
  const belege = belegeQuery.data?.items || [];
  const belegNachId = new Map(belege.map((b) => [b.id, b]));
  // Der Server liefert alle Auszahlungen; gezeigt wird das gewaehlte Jahr.
  // Was aus frueheren Jahren wegfaellt, wird beziffert statt still zu fehlen —
  // sonst wirkt ein Lohnkonto leer, obwohl Geld geflossen ist.
  const jahresPayouts = payouts.filter((p) => (p.zahlungsdatum || "").startsWith(String(year)));
  const frueher = payouts.length - jahresPayouts.length;
  const jahresSumme = jahresPayouts.reduce((s, p) => s + p.betrag, 0);
  const [showCreate, setShowCreate] = useState(false);
  const [editPayout, setEditPayout] = useState<TeamPayout | null>(null);
  const [eigenbelegFuer, setEigenbelegFuer] = useState<TeamPayout | null>(null);
  const deletePayout = (payout: TeamPayout) => {
    // Ein übermittelter Nachweis bleibt beim Steuerberater liegen — das muss man vorher wissen.
    const zusatz = payout.nachweis_status === "versendet"
      ? `\n\nDer Zahlungsnachweis ${payout.nachweis_nummer} ist schon bei DATEV — bitte dem Steuerberater Bescheid geben.`
      : "";
    if (window.confirm(`Auszahlung über ${euro(payout.betrag)} wirklich löschen?${zusatz}`)) remove.mutate(payout.id);
  };
  return (
    <section className="team-card team-payroll">
      <header className="team-card-head"><div><span className="team-section-label">Lohnkonto</span><h2>Verdienst &amp; Auszahlungen</h2></div><button type="button" className="team-button primary compact" onClick={() => setShowCreate(true)}>＋ Auszahlung</button></header>
      <div className="team-payroll-kpis">
        <PayrollKpi label="Offener Saldo" value={payroll.saldo_offen} tone="green" />
        <PayrollKpi label="Verdient gesamt" value={payroll.verdient_gesamt} />
        <PayrollKpi label="Ausgezahlt" value={payroll.ausgezahlt_gesamt} />
        <PayrollKpi label="Belegt" value={payroll.belegt} />
        <PayrollKpi label="Rechnung fehlt" value={payroll.rechnung_fehlt} tone={payroll.rechnung_fehlt > 0 ? "amber" : undefined} />
        <PayrollKpi label="Unberechnet" value={payroll.unberechnet} tone={payroll.unberechnet > 0 ? "amber" : undefined} />
      </div>
      <div className="team-subhead">
        <h3>Auszahlungen {year}</h3>
        <span>
          {jahresPayouts.length} Buchungen · {euro(jahresSumme)}
          {frueher > 0 && ` · ${frueher} aus früheren Jahren`}
          {payroll.letzte_auszahlung && ` · zuletzt ${shortDate(payroll.letzte_auszahlung)}`}
        </span>
      </div>
      {remove.isError && <p className="team-error">Die Auszahlung konnte nicht gelöscht werden.</p>}
      <div className="team-payout-list">
        {jahresPayouts.map((payout) => (
          <article key={payout.id}>
            <div><strong>{euro(payout.betrag)}</strong><span>{shortDate(payout.zahlungsdatum)} · {zahlwegLabel(payout)}</span>{payout.vermerk && <small>{payout.vermerk}</small>}</div>
            <div className="team-payout-meta">
              {payout.beleg_id ? (
                <>
                  <span className="team-beleg-ref">
                    {(() => {
                      const b = belegNachId.get(payout.beleg_id);
                      const wer = b?.lieferant || payout.beleg_lieferant || "Rechnung";
                      return b?.datum ? `${wer} · ${shortDate(b.datum)}` : wer;
                    })()}
                  </span>
                  <button
                    type="button"
                    className="team-text-action"
                    onClick={() => setDok({ titel: "Beleg zur Auszahlung", url: belegDateiUrl(payout.beleg_id as string), belegId: payout.beleg_id as string })}
                  >
                    Beleg ansehen
                  </button>
                </>
              ) : (
                <button type="button" className="team-text-action" onClick={() => setEigenbelegFuer(payout)} title="Ersatzbeleg für diese Auszahlung erzeugen">Eigenbeleg</button>
              )}
              <NachweisAktion memberId={memberId} payout={payout} onAnsehen={() => setDok(nachweisDok(memberId, payout))} />
              <button type="button" className="team-text-action" onClick={() => setEditPayout(payout)}>Ändern</button>
              <button type="button" className="team-text-danger" disabled={remove.isPending} onClick={() => deletePayout(payout)}>Löschen</button>
            </div>
          </article>
        ))}
        {!jahresPayouts.length && (
          <div className="team-state compact">
            <p>{frueher > 0 ? `Für ${year} keine Auszahlung — ${frueher} liegen in früheren Jahren.` : "Noch keine Auszahlungen erfasst."}</p>
          </div>
        )}
      </div>

      <BelegBlock memberId={memberId} owners={owners} year={year} belege={belege} query={belegeQuery} />

      {showCreate && <PayoutDialog memberId={memberId} owners={owners} onClose={() => setShowCreate(false)} />}
      {editPayout && <PayoutDialog memberId={memberId} owners={owners} payout={editPayout} onClose={() => setEditPayout(null)} />}
      {eigenbelegFuer && (
        <EigenbelegDialog
          memberId={memberId}
          memberName={memberName || ""}
          payout={eigenbelegFuer}
          onClose={() => setEigenbelegFuer(null)}
        />
      )}
      <DokumentLightbox dok={dok} onClose={() => setDok(null)} />
    </section>
  );
}

function PayrollKpi({ label, value, tone }: { label: string; value: number; tone?: "green" | "amber" }) {
  return <div><span>{label}</span><strong className={tone === "green" ? "money-positive" : tone === "amber" ? "money-warning" : ""}>{euro(value)}</strong></div>;
}

/**
 * Belege, die auf dieses Lohnkonto einzahlen.
 *
 * Gezeigt wird über `beleg.helfer_id` — nicht über den Hochlader. Genau diese
 * Summe steckt in der Kachel „Belegt"; ohne die Liste war nicht nachvollziehbar,
 * woraus sie sich zusammensetzt und was für „Rechnung fehlt" noch aussteht.
 */
function BelegBlock({ memberId, owners, year, belege, query }: {
  memberId: string;
  owners: TeamOwner[];
  year: number;
  belege: HelferBeleg[];
  query: { isLoading: boolean; isError: boolean };
}) {
  const { isLoading, isError } = query;
  const [bezahlen, setBezahlen] = useState<HelferBeleg | null>(null);
  const [bankzeileFuer, setBankzeileFuer] = useState<HelferBeleg | null>(null);
  const [dok, setDok] = useState<Dokument>(null);
  // Nur was noch nicht ausgezahlt ist. Bezahlte Rechnungen haengen sichtbar an
  // ihrer Auszahlung darueber — hier stand vorher dieselbe Zeile ein zweites Mal.
  const offen = belege.filter((b) => !b.auszahlung_id);
  const offeneSumme = offen.reduce((s, b) => s + (b.bruttobetrag || 0), 0);
  const ohneBankzeile = offen.filter((b) => !b.kontobewegung_id).length;
  const erledigt = belege.length - offen.length;

  return (
    <>
      <div className="team-subhead">
        <h3>Offene Rechnungen {year}</h3>
        <span>
          {offen.length ? `${offen.length} · ${euro(offeneSumme)}` : "keine"}
          {ohneBankzeile > 0 && ` · ${ohneBankzeile} ohne Bankzeile`}
          {erledigt > 0 && ` · ${erledigt} ausgezahlt`}
        </span>
      </div>
      {isLoading && <div className="team-state compact"><p>Rechnungen werden geladen …</p></div>}
      {isError && <p className="team-error">Die Rechnungen konnten nicht geladen werden.</p>}
      {!isLoading && !isError && !offen.length && (
        <div className="team-state compact">
          <p>{erledigt > 0
            ? `Alle ${erledigt} Rechnungen aus ${year} sind ausgezahlt — sie stehen oben an ihrer Buchung.`
            : `Für ${year} ist bei diesem Helfer keine Rechnung hinterlegt.`}</p>
        </div>
      )}
      <div className="team-payout-list">
        {offen.map((beleg) => (
          <article key={beleg.id}>
            <div>
              <strong>{euro(beleg.bruttobetrag)}</strong>
              <span>
                {shortDate(beleg.datum)} · {beleg.lieferant || "ohne Aussteller"}
                {beleg.projekt_title ? ` · ${beleg.projekt_title}` : ""}
              </span>
              {beleg.notiz && <small>{beleg.notiz}</small>}
            </div>
            <div className="team-payout-meta">
              {beleg.beleg_typ === "eigenbeleg" && <span className="team-pill">Eigenbeleg</span>}
              <span className={`team-pill${beleg.status === "verbucht" ? " success" : ""}`}>{beleg.status || "offen"}</span>
              {/* Ein "bezahlt" ohne Auszahlung stammt aus der Zeit vor der
                  Kopplung. Es bleibt sichtbar, damit die Nachbuchung
                  auffindbar ist statt still weiterzudriften. */}
              {beleg.is_bezahlt && (
                <span className="team-pill" title="Als bezahlt markiert, aber ohne Auszahlung auf dem Lohnkonto">
                  bezahlt, nicht gebucht
                </span>
              )}
              <button type="button" className="team-text-action" onClick={() => setBezahlen(beleg)}>
                Bezahlt buchen
              </button>
              {beleg.kontobewegung_id
                ? <span className="team-pill success" title="Einer Bankzeile zugeordnet">Bankzeile ✓</span>
                : <button type="button" className="team-text-action" onClick={() => setBankzeileFuer(beleg)}>Bankzeile suchen</button>}
              <button
                type="button"
                className="team-text-action"
                onClick={() => setDok({
                  titel: beleg.lieferant || "Helferrechnung",
                  unterzeile: beleg.datum ? shortDate(beleg.datum) : undefined,
                  url: belegDateiUrl(beleg.id),
                  belegId: beleg.id,
                })}
              >
                Ansehen
              </button>
            </div>
          </article>
        ))}
      </div>
      {bankzeileFuer && <BankzeileDialog beleg={bankzeileFuer} onClose={() => setBankzeileFuer(null)} />}
      <DokumentLightbox dok={dok} onClose={() => setDok(null)} />
      {bezahlen && (
        <BelegBezahltDialog
          memberId={memberId}
          beleg={bezahlen}
          owners={owners}
          onClose={() => setBezahlen(null)}
        />
      )}
    </>
  );
}

/**
 * Passende Bankzeile zu einer Helferrechnung suchen und zuordnen.
 *
 * Der Server liefert Bankzeilen mit **exakt dem Betrag** des Belegs — dieselbe
 * Suche wie im Belegregister und im Konto-Reiter, nur von hier aus erreichbar.
 * Ohne diesen Weg musste man das Mitarbeiterkonto verlassen, den Beleg im
 * Register wiederfinden und dort zuordnen; entsprechend blieben Bankzeilen und
 * Helferrechnungen dauerhaft unverknüpft.
 *
 * Zeilen, an denen schon ein anderer Beleg hängt, werden gezeigt aber gesperrt:
 * eine Überweisung gehört zu genau einem Beleg.
 */
function BankzeileDialog({ beleg, onClose }: { beleg: HelferBeleg; onClose: () => void }) {
  const { data: zeilen = [], isLoading, isError } = usePassendeBankzeilen(beleg.id, true);
  const zuordnen = useBankzeileZuordnen();

  return (
    <Dialog title="Bankzeile zuordnen" onClose={onClose}>
      <p className="team-hint">
        Gesucht wird nach Überweisungen über genau {euro(beleg.bruttobetrag)} — den Betrag von
        „{beleg.lieferant || "ohne Aussteller"}" vom {shortDate(beleg.datum)}.
      </p>
      {isLoading && <div className="team-state compact"><p>Bankzeilen werden gesucht …</p></div>}
      {isError && <p className="team-error">Die Bankzeilen konnten nicht geladen werden.</p>}
      {!isLoading && !isError && !zeilen.length && (
        <div className="team-state compact">
          <p>Keine Überweisung mit diesem Betrag gefunden. Entweder ist die Rechnung noch nicht bezahlt, oder der überwiesene Betrag weicht ab — dann hilft nur der Konto-Reiter in den Finanzen.</p>
        </div>
      )}
      <div className="team-payout-list">
        {zeilen.map((zeile) => (
          <article key={zeile.id}>
            <div>
              <strong>{euro(Math.abs(zeile.betrag))}</strong>
              <span>{shortDate(zeile.buchungsdatum)} · {zeile.gegenpartei_name || "ohne Empfänger"}</span>
              {zeile.verwendungszweck && <small>{zeile.verwendungszweck}</small>}
            </div>
            <div className="team-payout-meta">
              {zeile.hat_beleg
                ? <span className="team-pill muted" title="An dieser Bankzeile hängt bereits ein anderer Beleg">schon belegt</span>
                : (
                  <button
                    type="button"
                    className="team-text-action"
                    disabled={zuordnen.isPending}
                    onClick={() => zuordnen.mutate({ belegId: beleg.id, kontobewegungId: zeile.id }, { onSuccess: onClose })}
                  >
                    Zuordnen
                  </button>
                )}
            </div>
          </article>
        ))}
      </div>
      {zuordnen.isError && <p className="team-error">Die Bankzeile konnte nicht zugeordnet werden.</p>}
      <DialogActions onClose={onClose} />
    </Dialog>
  );
}

const GRUND_VORSCHLAEGE = [
  "Barauszahlung ohne Quittung",
  "Beleg des Helfers verloren gegangen",
  "Aushilfe ohne Rechnungsstellung",
  "Nachträglich erfasste Barzahlung",
];

/**
 * Ersatzbeleg für eine Auszahlung ohne Fremdbeleg.
 *
 * Der Server erzeugt daraus ein A4-PDF, legt es in Nextcloud unter
 * Belege/Eigenbelege/<Jahr>/ ab und schreibt die `beleg`-Zeile — dieselbe
 * Logik, die auch die Desktop-App benutzt. Ein Eigenbeleg berechtigt nicht
 * zum Vorsteuerabzug (§ 14 UStG); der Hinweis steht im PDF.
 */
function EigenbelegDialog({ memberId, memberName, payout, onClose }: {
  memberId: string;
  memberName: string;
  payout: TeamPayout;
  onClose: () => void;
}) {
  const erstellen = useCreateEigenbeleg();
  const [grund, setGrund] = useState("");
  const [empfaenger, setEmpfaenger] = useState(memberName);
  const [betrag, setBetrag] = useState(String(payout.betrag).replace(".", ","));
  const [datum, setDatum] = useState(payout.zahlungsdatum);
  const [zweck, setZweck] = useState(payout.vermerk || "Helfer-Auszahlung");

  const save = () => erstellen.mutate({
    helfer_id: memberId,
    member_lohn_auszahlung_id: payout.id,
    empfaenger: empfaenger.trim(),
    betrag: Number(betrag.replace(",", ".")) || 0,
    datum,
    grund: grund.trim(),
    verwendungszweck: zweck.trim(),
    ausgabe_typ: "helfer",
  }, { onSuccess: onClose });

  return (
    <Dialog title="Eigenbeleg erstellen" onClose={onClose}>
      <div className="team-form-grid">
        <Field label="Empfänger"><input value={empfaenger} onChange={(event) => setEmpfaenger(event.target.value)} /></Field>
        <Field label="Betrag (€)"><input inputMode="decimal" value={betrag} onChange={(event) => setBetrag(event.target.value)} /></Field>
        <Field label="Zahlungsdatum"><input type="date" value={datum} onChange={(event) => setDatum(event.target.value)} /></Field>
        <Field label="Verwendungszweck"><input value={zweck} onChange={(event) => setZweck(event.target.value)} /></Field>
        <Field label="Grund der Ausstellung *" full>
          <textarea rows={2} autoFocus value={grund} onChange={(event) => setGrund(event.target.value)} placeholder="Warum gibt es keinen Fremdbeleg?" />
        </Field>
      </div>
      <div className="team-vorschlaege">
        {GRUND_VORSCHLAEGE.map((vorschlag) => (
          <button type="button" key={vorschlag} onClick={() => setGrund(vorschlag)}>{vorschlag}</button>
        ))}
      </div>
      <p className="team-hint">
        Es entsteht ein PDF in Nextcloud und ein Beleg im Register. Ein Eigenbeleg berechtigt
        nicht zum Vorsteuerabzug (§ 14 UStG).
      </p>
      {erstellen.isError && <p className="team-error">Der Eigenbeleg konnte nicht erstellt werden.</p>}
      <DialogActions onClose={onClose} onSave={save} disabled={!grund.trim() || erstellen.isPending} busy={erstellen.isPending} />
    </Dialog>
  );
}
